import { Component, inject, signal } from '@angular/core';
import { QuillModule } from 'ngx-quill';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StudentPostsService } from '../../../services/student-posts.service';

/** Minimal option shape for the career/interest tag selects. */
export interface StudentPostTagOption {
  id: number;
  name: string;
}

/** Data passed in by the opener — the tag options it already has loaded. */
export interface StudentPostDialogData {
  careers?: StudentPostTagOption[];
  interests?: StudentPostTagOption[];
}

/** Keep in sync with MAX_POST_TEXT_LENGTH in studentPosts.controller.js. */
export const STUDENT_POST_MAX_LENGTH = 5000;

/**
 * Modal for a student to write a new post. A trimmed-down sibling of the
 * counselor PostDialog: no image uploads, drafts, grade/group restrictions,
 * or linked events — just content plus optional career/interest tags.
 * Closes with the created post on success.
 */
@Component({
  selector: 'app-student-post-dialog',
  imports: [
    QuillModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
  templateUrl: './student-post-dialog.html',
  styleUrl: './student-post-dialog.css',
})
export class StudentPostDialog {
  private dialogRef = inject(MatDialogRef<StudentPostDialog>);
  private studentPostService = inject(StudentPostsService);
  private snackBar = inject(MatSnackBar);
  private data = inject<StudentPostDialogData | null>(MAT_DIALOG_DATA, { optional: true });

  careers: StudentPostTagOption[] = this.data?.careers ?? [];
  interests: StudentPostTagOption[] = this.data?.interests ?? [];

  readonly maxLength = STUDENT_POST_MAX_LENGTH;

  /** Whether a submit request is in flight; disables the Post button. */
  isSubmitting = signal(false);

  /** Current plain-text length of the editor, for the counter and validation. */
  textLength = signal(0);

  form = new FormGroup({
    content: new FormControl(''),
    careers: new FormControl<number[]>([]),
    interests: new FormControl<number[]>([]),
  });

  /** Text-only toolbar — students can't upload images. */
  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  };

  quillEditor: any;

  onEditorCreated(editor: any): void {
    this.quillEditor = editor;
    editor.root.setAttribute('aria-label', 'Post content');
    editor.root.setAttribute('aria-multiline', 'true');
    editor.root.setAttribute('role', 'textbox');
  }

  onContentChanged(event: { text?: string }): void {
    this.textLength.set((event.text ?? '').trim().length);
  }

  get canSubmit(): boolean {
    const len = this.textLength();
    return !this.isSubmitting() && len > 0 && len <= this.maxLength;
  }

  submit(): void {
    if (!this.canSubmit) return;

    const { careers, interests } = this.form.value;
    const payload = {
      content: this.quillEditor?.root.innerHTML ?? this.form.value.content ?? '',
      careers: careers ?? [],
      interests: interests ?? [],
    };

    this.isSubmitting.set(true);
    this.studentPostService.createPost(payload).subscribe({
      next: (post) => {
        this.isSubmitting.set(false);
        this.snackBar.open('Post published.', 'Dismiss', { duration: 3000 });
        this.dialogRef.close(post);
      },
      error: (err) => {
        this.isSubmitting.set(false);
        const message =
          err?.status === 403
            ? "You don't have permission to create posts."
            : err?.error?.error || 'Failed to create post. Please try again.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
      },
    });
  }
}
